<template>
  <div class="alcohol-sales-container">
    <!-- 头部筛选区域 -->
    <div class="filter-section">
      <el-card shadow="never" class="filter-card">
        <div class="filter-header">
          <h3>酒类产品销售分析报告</h3>
          <div class="report-type">
            <el-radio-group v-model="reportType" @change="changeReportType">
              <el-radio-button label="year">年度报告</el-radio-button>
              <el-radio-button label="month">月度报告</el-radio-button>
            </el-radio-group>
          </div>
        </div>

        <div class="filter-controls">
          <!-- 时间选择 -->
          <div class="filter-group">
            <label v-if="reportType === 'year'">选择年份</label>
            <label v-else>选择月份</label>

            <el-date-picker
                v-if="reportType === 'year'"
                v-model="selectedYear"
                type="year"
                placeholder="选择年份"
                format="yyyy年"
                value-format="yyyy"
                :picker-options="yearPickerOptions"
                @change="handleYearChange">
            </el-date-picker>

            <el-date-picker
                v-else
                v-model="selectedMonth"
                type="month"
                placeholder="选择月份"
                format="yyyy年MM月"
                value-format="yyyy-MM"
                :picker-options="monthPickerOptions"
                @change="handleMonthChange">
            </el-date-picker>
          </div>

          <!-- 对比时间选择 -->
          <div class="filter-group" v-if="enableCompare">
            <label v-if="reportType === 'year'">对比年份</label>
            <label v-else>对比月份</label>

            <el-date-picker
                v-if="reportType === 'year'"
                v-model="compareYear"
                type="year"
                placeholder="选择对比年份"
                format="yyyy年"
                value-format="yyyy"
                :disabled="!enableCompare"
                :picker-options="compareYearPickerOptions"
                @change="getCompareData">
            </el-date-picker>

            <el-date-picker
                v-else
                v-model="compareMonth"
                type="month"
                placeholder="选择对比月份"
                format="yyyy年MM月"
                value-format="yyyy-MM"
                :disabled="!enableCompare"
                :picker-options="compareMonthPickerOptions"
                @change="getCompareData">
            </el-date-picker>
          </div>

          <!-- 酒类类型筛选 -->
          <div class="filter-group">
            <label>酒类类型</label>
            <el-select
                v-model="alcoholType"
                multiple
                collapse-tags
                placeholder="全部类型">
              <el-option
                  v-for="item in alcoholTypes"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value">
              </el-option>
            </el-select>
          </div>

          <!-- 操作按钮 -->
          <div class="action-buttons">
            <el-button
                type="primary"
                @click="getData"
                icon="el-icon-search">
              查询
            </el-button>
            <el-button
                @click="resetFilter"
                icon="el-icon-refresh">
              重置
            </el-button>
            <el-button
                type="info"
                @click="toggleCompare"
                icon="el-icon-data-line"
                :loading="compareLoading">
              {{ enableCompare ? '取消对比' : '数据对比' }}
            </el-button>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 数据概览 -->
    <div class="overview-section">
      <el-row :gutter="20">
        <el-col :span="6">
          <summary-card
              title="总销售额"
              :value="summary.totalAmount"
              :compare-value="enableCompare ? compareSummary.totalAmount : null"
              :show-compare="enableCompare"
              :compare-label="enableCompare ? formatCompareTime(compareTime) : ''"
              unit="元"
              icon="el-icon-money"
              color="#67C23A"
          />
        </el-col>
        <el-col :span="6">
          <summary-card
              title="总销售量"
              :value="summary.totalSales"
              :compare-value="enableCompare ? compareSummary.totalSales : null"
              :show-compare="enableCompare"
              :compare-label="enableCompare ? formatCompareTime(compareTime) : ''"
              unit="箱"
              icon="el-icon-shopping-bag-1"
              color="#409EFF"
          />
        </el-col>
        <el-col :span="6">
          <summary-card
              title="订单数量"
              :value="summary.orderCount"
              :compare-value="enableCompare ? compareSummary.orderCount : null"
              :show-compare="enableCompare"
              :compare-label="enableCompare ? formatCompareTime(compareTime) : ''"
              unit="笔"
              icon="el-icon-tickets"
              color="#E6A23C"
          />
        </el-col>
        <el-col :span="6">
          <summary-card
              title="累计客户"
              :value="summary.customerCount"
              :compare-value="enableCompare ? compareSummary.customerCount : null"
              :show-compare="enableCompare"
              :compare-label="enableCompare ? formatCompareTime(compareTime) : ''"
              unit="人"
              icon="el-icon-user"
              color="#F56C6C"
          />
        </el-col>
      </el-row>
    </div>

    <!-- 主图表区域 -->
    <div class="chart-section">
      <el-row :gutter="20">
        <!-- 销售趋势图 -->
        <el-col :span="24">
          <el-card class="main-chart-card">
            <div slot="header" class="chart-header">
              <span>{{ reportType === 'year' ? '年度' : '月度' }}销售趋势</span>
              <div class="chart-actions">
                <el-radio-group v-model="trendChartType" size="mini">
                  <el-radio-button label="amount">销售额</el-radio-button>
                  <el-radio-button label="sales">销售量</el-radio-button>
                </el-radio-group>
              </div>
            </div>
            <trend-chart
                :report-type="reportType"
                :chart-type="trendChartType"
                :trend-data="trendData"
                :compare-trend-data="compareTrendData"
                :enable-compare="enableCompare"
                :selected-time="reportType === 'year' ? selectedYear : selectedMonth"
                :compare-time="reportType === 'year' ? compareYear : compareMonth">
            </trend-chart>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="20" class="chart-row">
        <!-- 品类占比 -->
        <el-col :span="12">
          <el-card class="sub-chart-card">
            <div slot="header" class="chart-header">
              <span>酒类销售占比分析</span>
            </div>
            <category-pie-chart :category-data="categoryData"></category-pie-chart>
          </el-card>
        </el-col>

        <!-- 品类销售对比 -->
        <el-col :span="12">
          <el-card class="sub-chart-card">
            <div slot="header" class="chart-header">
              <span>客户情况分析</span>
            </div>
            <category-bar-chart :customer-data="comparisonData"></category-bar-chart>

          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 产品销售排行 -->
    <product-ranking-table
        :products="productLists"
        :loading="listLoading"
        :alcohol-type="alcoholType"
       >
    </product-ranking-table>
  </div>
</template>

<script>
import SummaryCard from './utils/SummaryCard.vue'
import TrendChart from './utils/TrendChart.vue'
import CategoryPieChart from './utils/CategoryPie.vue'
import CategoryBarChart from './utils/CategoryBar.vue'
import ProductRankingTable from './utils/ProductRankingTable.vue'


export default {
  components: {
    SummaryCard,
    TrendChart,
    CategoryPieChart,
    CategoryBarChart,
    ProductRankingTable
  },
  filters: {
    formatNumber(value) {
      if (!value) return '0'
      return Number(value).toLocaleString()
    },
    formatCurrency(value) {
      if (!value) return '¥0'
      return '¥' + Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  },
  data() {
    const currentYear = new Date().getFullYear()
    const currentMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`

    return {

      yearPickerOptions: {
        disabledDate(time) {
          const year = new Date(time).getFullYear();
          const currentYear = new Date().getFullYear();
          return year < 2024 || year > currentYear;
        }
      },
      monthPickerOptions: {
        disabledDate(time) {
          const date = new Date(time);
          const year = date.getFullYear();
          const month = date.getMonth() + 1;
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth() + 1;

          // 早于2024年1月或晚于当前年月
          return (
              year < 2024 ||
              (year === 2024 && month < 1) ||
              year > currentYear ||
              (year === currentYear && month > currentMonth)
          );
        }
      },
      compareTime: '',
      compareLoading:false,
      reportType: 'year',
      selectedYear: String(currentYear),
      selectedMonth: currentMonth,
      enableCompare: false,
      compareYear: String(currentYear - 1),
      compareMonth: `${currentYear - 1}-${String(new Date().getMonth() + 1).padStart(2, '0')}`,
      alcoholType: [],
      alcoholTypes: [],
      trendChartType: 'amount',

      // 数据相关
      summary: {
        totalAmount: 0,
        totalSales: 0,
        orderCount: 0,
        customerCount: 0
      },
      compareSummary: {
        totalAmount: 0,
        totalSales: 0,
        orderCount: 0,
        customerCount: 0
      },
      trendData: {
        xAxis: [],
        amountData: [],
        salesData: []
      },
      compareTrendData: {
        xAxis: [],
        amountData: [],
        salesData: []
      },
      categoryData: [],
      comparisonData: [],
      productLists: [],
      listLoading: false,

      // 详情抽屉
      detailVisible: false,
      currentProduct: {
        id: '',
        productName: '',
        picture: [],
        productClassifyName: '',
        volume: 0,
        abv: 0,
        price: 0,
        sales: 0,
        totalSales: 0
      },
      currentProductCompare: {
        id: '',
        productName: '',
        picture: [],
        productClassifyName: '',
        volume: 0,
        abv: 0,
        price: 0,
        sales: 0,
        totalSales: 0
      },

      // 图表实例
      productHistoryChart: null
    }
  },
  async created() {
    await this.fetchCategoryList()
    this.getData()
  },
  beforeDestroy() {
    if (this.productHistoryChart) {
      this.productHistoryChart.dispose()
    }
  },
  computed: {
    compareYearPickerOptions() {
      return {
        disabledDate: (time) => {
          const year = new Date(time).getFullYear();
          const currentYear = new Date().getFullYear();
          const isSameYear = year.toString() === this.selectedYear;
          return (
              year < 2024 ||
              year > currentYear ||
              isSameYear
          );
        }
      };
    },
    compareMonthPickerOptions() {
      return {
        disabledDate: (time) => {
          const date = new Date(time);
          const year = date.getFullYear();
          const month = date.getMonth() + 1;
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear();
          const currentMonth = currentDate.getMonth() + 1;
          const isSameMonth = `${year}-${month.toString().padStart(2, '0')}` === this.selectedMonth;

          return (
              year < 2024 ||
              (year === 2024 && month < 1) ||
              year > currentYear ||
              (year === currentYear && month > currentMonth) ||
              isSameMonth
          );
        }
      };
    }
  },
  methods: {
    handleYearChange(val) {
      if (this.enableCompare && val === this.compareYear) {
        this.$message.warning('不能选择相同的年份进行对比');
        this.selectedYear = '';
        return;
      }
      this.getData();
    },
    handleMonthChange(val) {
      if (this.enableCompare && val === this.compareMonth) {
        this.$message.warning('不能选择相同的月份进行对比');
        this.selectedMonth = '';
        return;
      }
      this.getData();
    },
    formatCompareTime(timeStr) {
      if (!timeStr) return ''
      if (this.reportType === 'year') {
        return `${timeStr}年`
      } else {
        const [year, month] = timeStr.split('-')
        return `${year}年${month.padStart(2, '0')}月`
      }
    },
    async fetchCategoryList() {
      try {
        const res = await this.$http.get('/productClassify/classifyList')

        if (res.data && res.data.success && Array.isArray(res.data.resultObj)) {
          this.alcoholTypes = res.data.resultObj.map(item => ({
            label: item.productClassifyName,
            value: item.id,
            original: item
          }))
        } else {
          throw new Error('接口返回数据格式不符合预期')
        }
      } catch (error) {
        this.$message.error(`获取分类列表失败: ${error.message}`)
        this.alcoholTypes = []
      }
    },

    async getData() {
      this.listLoading = true
      try {
        const params = {
          reportType: this.reportType,
          timeValue: this.reportType === 'year' ? this.selectedYear : this.selectedMonth,
          alcoholTypes: this.alcoholType.join(',')
        }

        // 1. 获取汇总数据
        const summaryRes = await this.$http.get('/alcohol-sales/summary', {params})
        const res = summaryRes.data.resultObj || {}

        this.summary.totalAmount = res.totalAmount || 0.00
        this.summary.totalSales = res.totalSales || 0
        this.summary.orderCount = res.orderCount || 0
        this.summary.customerCount = res.customerCount || 0

        // 2. 其他数据获取
        const trendRes = await this.$http.get('/alcohol-sales/trend', {params})
        const categoryRes = await this.$http.get('/alcohol-sales/category-percentage', {params})
        const comparisonRes = await this.$http.get('/alcohol-sales/category-comparison', {params})
        const productsRes = await this.$http.get('/alcohol-sales/top-products', {params})

        this.trendData = trendRes.data.resultObj
        this.categoryData = categoryRes.data.resultObj
        this.comparisonData = comparisonRes.data.resultObj
        this.productLists = productsRes.data.resultObj

        // 如果有对比数据，更新对比图表
        if (this.enableCompare) {
          this.getCompareData()
        }

      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      } finally {
        this.listLoading = false
      }
    },

    async getCompareData() {
      if (!this.enableCompare) return

      try {
        const params = {
          reportType: this.reportType,
          timeValue: this.reportType === 'year' ? this.compareYear : this.compareMonth,
          alcoholTypes: this.alcoholType.join(',')
        }

        // 1. 获取对比汇总数据
        const summaryRes = await this.$http.get('/alcohol-sales/summary', {params})
        const res = summaryRes.data.resultObj || {}

        this.compareSummary.totalAmount = res.totalAmount || 0.00
        this.compareSummary.totalSales = res.totalSales || 0
        this.compareSummary.orderCount = res.orderCount || 0
        this.compareSummary.customerCount = res.customerCount || 0

        // 2. 获取对比趋势数据
        const trendRes = await this.$http.get('/alcohol-sales/trend', {params})
        this.compareTrendData = trendRes.data.resultObj

      } catch (error) {
        console.error('获取对比数据失败:', error)
        this.$message.error('获取对比数据失败')
      }
    },


      async toggleCompare() {
        try {
          // 先显示加载状态
          this.compareLoading = true

          if (this.enableCompare) {
            // 当前是对比状态，点击后取消对比
            this.enableCompare = false
            // 可以保留对比数据，但图表会自动切换回单条模式
          } else {
            // 当前是非对比状态，点击后启用对比
            this.enableCompare = true

            // 确保有对比时间值
            if (!this.compareYear && !this.compareMonth) {
              // 如果没有设置对比时间，自动设置为上一个周期
              if (this.reportType === 'year') {
                this.compareYear = String(Number(this.selectedYear) - 1)
              } else {
                const [year, month] = this.selectedMonth.split('-')
                const prevMonth = month === '01' ? '12' : String(Number(month) - 1).padStart(2, '0')
                const prevYear = month === '01' ? String(Number(year) - 1) : year
                this.compareMonth = `${prevYear}-${prevMonth}`
              }
            }

            // 获取对比数据
            await this.getCompareData()
          }
        } catch (error) {
          console.error('切换对比模式失败:', error)
          this.$message.error('切换对比模式失败')
          // 出错时回滚状态
          this.enableCompare = !this.enableCompare
        } finally {
          this.compareLoading = false
        }
      },




    changeReportType() {
      const currentYear = new Date().getFullYear()
      this.compareYear = String(currentYear - 1)
      this.compareMonth = `${currentYear - 1}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
      this.getData()
    },

    resetFilter() {
      const currentYear = new Date().getFullYear()
      const currentMonth = `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}`

      this.selectedYear = String(currentYear)
      this.selectedMonth = currentMonth
      this.compareYear = String(currentYear - 1)
      this.compareMonth = `${currentYear - 1}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
      this.alcoholType = []
      this.getData()
    },

    async getProductCompareData(productId) {
      try {
        const params = {
          productId: productId,
          reportType: this.reportType,
          timeValue: this.reportType === 'year' ? this.compareYear : this.compareMonth
        }

        const res = await this.$http.get('/alcohol-sales/product-detail', {params})
        this.currentProductCompare = res.data.resultObj || {}
      } catch (error) {
        console.error('获取产品对比数据失败:', error)
        this.$message.error('获取产品对比数据失败')
      }
    },

    getCompareValue(current, compare) {
      if (!current || !compare) return ''
      const diff = current - compare
      const percent = (diff / compare * 100).toFixed(2)
      const isPositive = diff >= 0
      const symbol = isPositive ? '+' : ''
      const className = isPositive ? 'positive' : 'negative'

      return `<span class="${className}">${symbol}${percent}% (${symbol}${diff})</span>`
    },

    updateProductHistoryChart() {
      const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']
      const currentData = months.map(() => Math.floor(Math.random() * 50) + 20)
      const compareData = months.map(() => Math.floor(Math.random() * 50) + 15)

      const series = [{
        name: this.reportType === 'year' ? this.selectedYear : this.selectedMonth,
        type: 'bar',
        data: currentData,
        itemStyle: {
          color: '#409EFF'
        }
      }]

      if (this.enableCompare) {
        series.push({
          name: this.reportType === 'year' ? this.compareYear : this.compareMonth,
          type: 'bar',
          data: compareData,
          itemStyle: {
            color: '#67C23A'
          }
        })
      }

      const option = {
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: series.map(item => item.name)
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: months
        },
        yAxis: {
          type: 'value',
          name: '销售量(瓶)'
        },
        series: series
      }

      this.productHistoryChart.setOption(option)
    }
  }
}
</script>

<style lang="scss" scoped>
.alcohol-sales-container {
  padding: 20px;
  background-color: #f5f7fa;
}

.filter-section {
  margin-bottom: 20px;

  .filter-card {
    border-radius: 8px;
    border: 1px solid #ebeef5;

    .filter-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      padding-bottom: 10px;
      border-bottom: 1px solid #ebeef5;

      h3 {
        margin: 0;
        font-size: 18px;
        color: #303133;
      }

      .report-type {
        margin-left: 20px;
      }
    }

    .filter-controls {
      display: flex;
      flex-wrap: wrap;
      gap: 20px;

      .filter-group {
        display: flex;
        align-items: center;

        label {
          margin-right: 10px;
          font-size: 14px;
          color: #606266;
          white-space: nowrap;
        }
      }

      .action-buttons {
        margin-left: auto;
      }
    }
  }
}

.overview-section {
  margin-bottom: 20px;
}

.chart-section {
  margin-bottom: 20px;

  .main-chart-card {
    margin-bottom: 20px;
    border-radius: 8px;

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-weight: bold;
    }
  }

  .chart-row {
    margin-top: 20px;
  }

  .sub-chart-card {
    border-radius: 8px;
    height: 100%;

    .chart-header {
      font-weight: bold;
    }
  }
}

.product-detail-content {
  padding: 20px;

  .product-detail-info {
    h3 {
      margin-top: 0;
      margin-bottom: 15px;
    }

    .detail-item {
      margin-bottom: 10px;

      .detail-label {
        font-weight: bold;
        color: #606266;
      }
    }
  }

  .sales-detail-section {
    margin-top: 20px;

    h4 {
      margin-top: 0;
      margin-bottom: 15px;
    }

    .data-card {
      background-color: #f5f7fa;
      padding: 15px;
      border-radius: 4px;
      text-align: center;

      .data-title {
        font-size: 14px;
        color: #909399;
        margin-bottom: 5px;
      }

      .data-value {
        font-size: 18px;
        font-weight: bold;
        color: #303133;
      }

      .data-compare {
        font-size: 12px;
        margin-top: 5px;

        .positive {
          color: #67c23a;
        }

        .negative {
          color: #f56c6c;
        }
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// ProductSales.vue?41e4105b